import React, { useState, useEffect } from 'react'
import { Order as OrderComponent } from './Order'
import { useLogin } from '../hooks'
import { graphql } from '../gql'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useGQLFetcher } from '../context/GQLClientProvider'
import { useIntersect } from '../lib/hooks/useIntersect'
import { Button } from './shadcn/Button'
import { faArrowDown } from '@fortawesome/pro-regular-svg-icons'

const getOrderQuery = graphql(`
  query OrderHistoryItem($id: Int!) {
    order(id: $id) {
      data {
        id
        ...OrderListItem_Order
      }
    }
  }
`)

const ITEMS_PER_PAGE = 20
const MAX_ITEMS = 5000 // it is safe to set around 25k items to avoid 4mb limit, but we assume that edge-case is 5 accounts that picks 5k orders each.

export function useOrdersHistory() {
  const { user } = useLogin()

  const addOrderToHistory = (order: any) => {
    if (!user) return
    const key = `ordersHistory_${user.id}`
    const stored = localStorage.getItem(key)
    let history: any[] = []
    if (stored) {
      try {
        history = JSON.parse(stored).slice(0, MAX_ITEMS)
      } catch {
        history = []
      }
    }
    history = history.filter((o: any) => o.id !== order.id)
    const historyOrder = { id: order.id, orderHistoryTimestamp: Date.now() }
    history.unshift(historyOrder)
    history = history.slice(0, MAX_ITEMS)
    localStorage.setItem(key, JSON.stringify(history))
  }

  return { addOrderToHistory }
}

export const OrdersHistory = () => {
  const { user } = useLogin()
  const { measureRef, isIntersecting } = useIntersect()
  useEffect(() => {
    if (!user) return
    const key = `ordersHistory_${user.id}`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        setOrderHistory(JSON.parse(stored))
      } catch (error) {
        console.error('Failed to parse orders history', error)
      }
    } else {
      setOrderHistory([])
    }
  }, [user])

  const [orderHistory, setOrderHistory] = useState<{ id: number; orderHistoryTimestamp: number }[]>([])
  const getOrder = useGQLFetcher(getOrderQuery)

  const { data, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['orderHistoryItem'],
    queryFn: ({ pageParam = 0 }) => {
      return Promise.all(
        orderHistory
          .slice(pageParam * ITEMS_PER_PAGE, (pageParam + 1) * ITEMS_PER_PAGE)
          .map(o => getOrder({ id: o.id })),
      ).then(orders =>
        orders.map((o, i) => ({
          ...o.order.data,
          orderHistoryTimestamp: orderHistory[i].orderHistoryTimestamp,
        })),
      )
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.length === ITEMS_PER_PAGE ? pages.length + 1 : undefined
    },
    enabled: orderHistory.length > 0,
  })

  const orders = data?.pages.flatMap(page => page.flatMap(p => p))

  useEffect(() => {
    if (isIntersecting) {
      fetchNextPage()
    }
  }, [isIntersecting])

  return (
    <div className="p-4 grid gap-4 orderColumns">
      {orders?.map(o => <OrderComponent key={o.id} order={o} historyItemTimestamp={o.orderHistoryTimestamp || 0} />)}
      {hasNextPage && (
        <Button
          ref={measureRef}
          id={'next-orders'}
          variant="default"
          icon={faArrowDown}
          title={'Načíst další'}
          disabled={isFetchingNextPage}
          onClick={() => fetchNextPage()}>
          {'Načíst další'}
        </Button>
      )}
    </div>
  )
}
